import { useState, useCallback, useEffect, useRef,useMemo } from 'react';
import { useLazyGetMaterialCatalogDetailQuery, useLazyGetMaterialCatalogMovementQuery, useLazyGetMaterialCatalogOtherCompanyQuery } from '../../../../api/endpoints/materialCatalog';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Roles } from '../../../../utils/roleEnums';
import { useLanguage } from '../../../../contexts/LanguageContext';

const defaultLazy = { first: 0, rows: 10, page: 0 };

const initialFilterState = {
    apiFilters: {},
    hasApplied: false
};

export const useMaterialCatalog = (activeIndex) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const { hasAnyRole } = usePermissions();
    const isDealer = hasAnyRole([Roles.DEALER, Roles.SERVICE_DEALER, Roles.SALES_DEALER]);
    const toastRef = useRef(null);

    const [filterState, setFilterState] = useState(initialFilterState);

    const [lazyDetail, setLazyDetail] = useState(defaultLazy);
    const [lazyMovement, setLazyMovement] = useState(defaultLazy);
    const [lazyOther, setLazyOther] = useState(defaultLazy);

    const [getDetail, { data: detailData, isLoading: detailLoading, reset: resetDetail }] = useLazyGetMaterialCatalogDetailQuery();
    const [getMovement, { data: movementData, isLoading: movementLoading, reset: resetMovement }] = useLazyGetMaterialCatalogMovementQuery();
    const [getOther, { data: otherData, isLoading: otherLoading }] = useLazyGetMaterialCatalogOtherCompanyQuery();

    const resetDetailRef = useRef(resetDetail);
    const resetMovementRef = useRef(resetMovement);
    useEffect(() => {
        resetDetailRef.current = resetDetail;
    }, [resetDetail]);
    useEffect(() => {
        resetMovementRef.current = resetMovement;
    }, [resetMovement]);

    // Detail tab
    useEffect(() => {
        if (activeIndex !== 0) return;
        const { kunnr, partCode } = filterState.apiFilters;
        if (!isDealer && !filterState.hasApplied) return;
        if (!isDealer && !kunnr && !partCode) return;
        getDetail({
            kunnr: kunnr || '',
            partCode: partCode || '',
            languageId: getCurrentLanguageId(),
            pageNumber: lazyDetail.page + 1,
            pageSize: lazyDetail.rows
        });
    }, [activeIndex, filterState, lazyDetail, isDealer]); // eslint-disable-line

    // Movement tab
    useEffect(() => {
        if (activeIndex !== 1) return;
        const { kunnr, partCode } = filterState.apiFilters;
        if (!isDealer && !filterState.hasApplied) return;
        if (!isDealer && !kunnr && !partCode) return;
        getMovement({
            dealerCode: kunnr || '',
            partCode: partCode || '',
            pageNumber: lazyMovement.page + 1,
            pageSize: lazyMovement.rows
        });
    }, [activeIndex, filterState, lazyMovement, isDealer]); // eslint-disable-line

    // Other tab
    useEffect(() => {
        if (activeIndex !== 2) return;
        const { kunnr, partCode } = filterState.apiFilters;
        if (isDealer && !partCode) return; 
        getOther({
            dealerCode: kunnr || filterState.apiFilters.dealerCode || null,
            partCode: partCode || '',
            languageId: getCurrentLanguageId(),
            pageNumber: lazyOther.page + 1,
            pageSize: lazyOther.rows
        });
    }, [activeIndex, filterState, lazyOther]); // eslint-disable-line

    const handleApplyFilters = useCallback(
        (updated) => {
            const newFilters = {};
            Object.entries(updated).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    newFilters[key] = value;
                }
            });

            // Tab 0/1'de admin için kunnr veya partCode zorunlu
            if (!isDealer && (activeIndex === 0 || activeIndex === 1)) {
                if (!newFilters.kunnr && !newFilters.partCode) {
                    toastRef.current?.show({
                        severity: 'warn',
                        summary: th('common.warning'),
                        detail: th('materialCatalog.warnings.selectDealerOrPart'),
                        life: 3000
                    });
                    return;
                }
            }

            resetDetailRef.current();
            resetMovementRef.current();
            setFilterState({ apiFilters: newFilters, hasApplied: true });
            setLazyDetail(defaultLazy);
            setLazyMovement(defaultLazy);
            setLazyOther(defaultLazy);
        },
        [isDealer, activeIndex, th]
    ); // eslint-disable-line

   const handleResetFilters = useCallback(() => {
    if (isDealer) {

        setFilterState(initialFilterState);
        setLazyDetail(defaultLazy);
        setLazyMovement(defaultLazy);
        setLazyOther(defaultLazy);
    } else {

        resetDetailRef.current();
        resetMovementRef.current();
        setFilterState(initialFilterState);
        setLazyDetail(defaultLazy);
        setLazyMovement(defaultLazy);
        setLazyOther(defaultLazy);
    }
}, [isDealer]);

    const onPageDetail = useCallback((e) => setLazyDetail({ first: e.first, rows: e.rows, page: e.page }), []);
    const onPageMovement = useCallback((e) => setLazyMovement({ first: e.first, rows: e.rows, page: e.page }), []);
    const onPageOther = useCallback((e) => setLazyOther({ first: e.first, rows: e.rows, page: e.page }), []);


     const detailExportParams = useMemo(() => ({
        kunnr: filterState.apiFilters.kunnr || null,
        partCode: filterState.apiFilters.partCode || null,
        languageId: getCurrentLanguageId()
    }), [filterState.apiFilters, getCurrentLanguageId]);
 
    const movementExportParams = useMemo(() => ({
        dealerCode: filterState.apiFilters.kunnr || null,
        partCode: filterState.apiFilters.partCode || null
    }), [filterState.apiFilters]);
 
    const otherExportParams = useMemo(() => ({
        dealerCode: filterState.apiFilters.kunnr || filterState.apiFilters.dealerCode || null,
        partCode: filterState.apiFilters.partCode || null,
        languageId: getCurrentLanguageId()
    }), [filterState.apiFilters, getCurrentLanguageId]);

    return {
        toastRef,

        detailItems: isDealer ? detailData?.items ?? [] : filterState.hasApplied ? detailData?.items ?? [] : null,
        detailTotalCount: isDealer ? detailData?.totalCount ?? 0 : filterState.hasApplied ? detailData?.totalCount ?? 0 : 0,
        detailLoading,
        lazyParamsDetail: lazyDetail,
        onPageDetail,

        movementItems: isDealer ? movementData?.items ?? [] : (filterState.hasApplied ? movementData?.items ?? [] : null),
        movementTotalCount: isDealer ? movementData?.totalCount ?? 0 : (filterState.hasApplied ? movementData?.totalCount ?? 0 : 0),
        movementLoading,
        lazyParamsMovement: lazyMovement,
        onPageMovement,

        otherItems: isDealer ? (filterState.apiFilters.partCode ? otherData?.items ?? [] : null) : otherData?.items ?? [],
        otherTotalCount: isDealer ? (filterState.apiFilters.partCode ? otherData?.totalCount ?? 0 : 0) : otherData?.totalCount ?? 0,
        otherLoading,
        lazyParamsOther: lazyOther,
        onPageOther,

        handleApplyFilters,
        handleResetFilters,
        isDealer,

        detailExportParams,
        movementExportParams,
        otherExportParams
    };
};
